<template>
  <form
    name="reset-password"
    class="app-login-reset-pass"
    @submit.prevent="handlerSubmit"
  >
    <template v-if="part === 'reset'">
      <h2
        class="authentication__title text-center"
        v-text="'Сбросить пароль'"
      />
      <div class="authentication__field-group">
        <div class="app-input-group">
          <app-input-control
            placeholder="example@gmail.com"
            type="text"
            title="Email"
            tooltip="Введите email"
            v-model="form.email"
          ></app-input-control>
          <small
            v-if="$v.form.email.$dirty && $v.form.email.$error"
            class="app-input-group__invalid"
          >
            Введите корректную почту
          </small>
        </div>
      </div>
      <div class="authentication__field-group">
        <app-button-control
          :loading="isLoading"
          nativeType="submit"
          isWhiteLoader
          size="large"
          type="secondary"
          is-block
        >
          Сбросить
        </app-button-control>
        <app-button-control
          type="link"
          @click="$emit('open', 'app-login-email')"
          >Вернуться назад</app-button-control
        >
      </div>
    </template>

    <template v-if="part === 'code-check'">
      <h2 class="authentication__title text-center" v-text="'Введите код'" />
      <app-security-code v-model="code"></app-security-code>
      <div
        class="authentication__helper-text"
        v-text="'Пароль сброшен, проверьте свою почту'"
      />
    </template>

    <template v-if="part === 'change'">
      <div class="app-login-reset-pass__change-pass" v-if="code.length === 6">
        <h2
          class="authentication__title text-center"
          v-text="'Введите новый пароль'"
        />
        <div class="authentication__field-group">
          <app-input-control
            type="password"
            placeholder="_"
            title="Новый пароль"
            tooltip="Введите пароль"
            v-model="password"
          ></app-input-control>
          <app-password-complexity
            :password="password"
            class="app-input-group__password-complexity"
          ></app-password-complexity>
        </div>

        <div class="authentication__field-group">
          <app-input-control
            type="password"
            placeholder="_"
            title="Повторите пароль"
            tooltip="Повторите пароль"
            v-model="repeatPassword"
          ></app-input-control>
          <small
            v-if="$v.password.$dirty && !$v.password.required"
            class="app-input-group__invalid"
          >
            Введите пароль
          </small>
          <small
            v-else-if="$v.password.$dirty && !$v.password.minLength"
            class="app-input-group__invalid"
          >
            Введите больше {{ $v.password.$params.minLength.min }} символов.
            Сейчас он
            {{ password.length }}
          </small>
          <small
            v-if="!$v.repeatPassword.sameAsPassword"
            class="app-input-group__invalid"
          >
            Пароли не совпадают
          </small>
        </div>
      </div>
      <div class="authentication__field-group">
        <app-button-control
          v-if="code.length === 6"
          :loading="isLoading"
          isWhiteLoader
          nativeType="submit"
          size="large"
          type="secondary"
          is-block
        >
          Отправить
        </app-button-control>
        <app-button-control type="link" @click="backToReset"
          >Вернуться назад</app-button-control
        >
      </div>
    </template>

    <div
      v-if="errorMessage"
      class="text-negative text-center"
      v-text="errorMessage"
    />
  </form>
</template>

<script>
import { authMapper } from '@/store/auth';
import { email, required, minLength, sameAs } from 'vuelidate/lib/validators';
import AppSecurityCode from '@/components/ui/AppSecurityCode.vue';

const createDefaultData = () => ({
  email: '',
});

export default {
  name: 'AppLoginResetPassword',
  validations: {
    form: {
      email: { email, required },
    },
    password: { minLength: minLength(6), required },
    repeatPassword: { sameAsPassword: sameAs('password') },
    passwordGroup: ['password', 'repeatPassword'],
  },
  components: {
    AppSecurityCode,
    AppPasswordComplexity: () =>
      import('@/components/ui/AppPasswordComplexity'),
    AppInputControl: () => import('@/components/ui/AppInputControl'),
    AppButtonControl: () => import('@/components/ui/AppButtonControl'),
  },
  props: {
    isBack: Boolean,
  },
  data: () => ({
    form: createDefaultData(),
    isLoading: false,
    errorMessage: '',
    isSuccess: false,
    code: '',
    part: 'reset', // reset, change, code-check
    repeatPassword: '',
    password: '',
  }),
  watch: {
    code(value) {
      if (value.length === 6) {
        this.codeCheck();
      }
    },
  },
  beforeDestroy() {
    this.form = createDefaultData();
  },
  methods: {
    ...authMapper.mapActions([
      'passwordReset',
      'passwordChange',
      'passwordCodeCheck',
    ]),
    /**
     * Сброс значений
     */
    backToReset() {
      this.password = '';
      this.repeatPassword = '';
      this.code = '';
      this.part = 'reset';
      //   this.$emit('open', 'app-login-email');
    },
    /**
     * Событие отправки формы
     */
    async handlerSubmit() {
      if (this.part === 'reset') {
        await this.resetPassword();
      }
      if (this.part === 'change' && this.code.length === 6) {
        await this.changePassword();
      }
    },
    /**
     * Сброс пароля
     */
    async resetPassword() {
      if (this.$v.form.$invalid) {
        this.$v.$touch();
        return;
      }
      try {
        this.isLoading = true;
        this.errorMessage = null;
        await this.passwordReset(this.form);
        this.isSuccess = true;
        this.part = 'code-check';
      } catch (error) {
        // eslint-disable-next-line prefer-destructuring
        this.errorMessage = error[0];
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * Проверка кода смены пароля
     */
    async codeCheck() {
      try {
        this.isLoading = true;
        this.errorMessage = null;
        const form = {
          email: this.form.email,
          code: this.code,
        };
        await this.passwordCodeCheck(form);
        this.part = 'change';
      } catch (error) {
        // eslint-disable-next-line prefer-destructuring
        this.errorMessage = error[0];
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * Смена пароля
     */
    async changePassword() {
      if (this.$v.passwordGroup.$invalid) {
        this.$v.$touch();
        return;
      }
      try {
        this.isLoading = true;
        this.errorMessage = null;
        const form = {
          email: this.form.email,
          password: this.password,
          code: this.code,
        };
        await this.passwordChange(form);
        this.form = createDefaultData();
        this.part = 'done';
        this.$emit('submit');
      } catch (error) {
        // eslint-disable-next-line prefer-destructuring
        this.errorMessage = error[0];
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.app-login-reset-pass {
  &__change-pass {
    margin-top: 20px;
  }
}
</style>
